import type { FC } from "react";
import { useMemo, useEffect } from "react";
import { FormControl, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FoodsSelectServerPagination } from "@foodpilot/foods";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useSearchSsq } from "src/api/sure-api/ssq/useSearchSsq";
import { useGetAllSsq } from "src/api/sure-api/ssq/useGetAllSsq";
import { getSsqOptionName } from "src/utils/ssqTitle";
import type { ComponentAppearance } from "src/components/appearance";
import { FoodsSelectTriggerAdapter } from "src/components/EntityForm/FoodsSelectTriggerAdapter";

export interface SsqOption {
    id: number;
    title: string;
    displayTitle?: string | null;
    [key: string]: unknown; // Pour compatibilité avec le schema Zod passthrough
}

interface SsqAutocompleteProps {
    value: SsqOption | null;
    onChange: (option: SsqOption | null) => void;
    placeholder?: string;
    disabled?: boolean;
    excludeIds?: number[];
    entityType?: "company" | "product" | "packaging";
    publishedOnly?: boolean;
    appearance?: ComponentAppearance;
    accessibleLabel?: string;
}

export const SsqAutocomplete: FC<SsqAutocompleteProps> = ({
    value,
    onChange,
    placeholder,
    disabled = false,
    excludeIds = [],
    entityType,
    publishedOnly = false,
    appearance = "legacy",
    accessibleLabel,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { search, delayedSearch, setSearch } = useDelayedSearch();

    // Use search hook when there's a search term, otherwise use the regular get all
    const searchEnabled = delayedSearch.length >= 2;
    const loadInitialEnabled = delayedSearch.length < 2;

    const { ssqs: searchSsqs = [], isLoading: searchLoading } = useSearchSsq({
        searchTerm: delayedSearch,
        enabled: searchEnabled,
        itemsPerPage: 20,
        entityType,
        publishedOnly,
    });

    const { ssqs: allSsqs = [], isLoading: allLoading } = useGetAllSsq({
        itemsPerPage: 20,
        enabled: loadInitialEnabled,
        entityType,
        publishedOnly,
    });

    // Determine which data to use with more explicit logic
    const ssqs = useMemo(() => {
        if (searchEnabled) {
            return searchSsqs;
        } else {
            return allSsqs;
        }
    }, [searchEnabled, searchSsqs, allSsqs]);

    const isLoading = searchEnabled ? searchLoading : allLoading;
    // Filter out excluded IDs and ensure current value is always available
    const filteredSsqs = useMemo(() => {
        let filtered = ssqs.filter((ssq) => !excludeIds.includes(ssq.id));

        // Always include the current value if it exists and isn't already in the list
        if (value && !filtered.some((ssq) => ssq.id === value.id)) {
            filtered = [value, ...filtered];
        }

        return filtered;
    }, [ssqs, excludeIds, value]);

    const isSearchFound = () => {
        // Pour une recherche côté serveur, on retourne toujours true
        // car les résultats sont déjà filtrés par le serveur
        return true;
    };

    // Component effect for managing search state
    useEffect(() => {
        // Update search state when dependencies change
    }, [
        search,
        delayedSearch,
        searchEnabled,
        loadInitialEnabled,
        ssqs.length,
        filteredSsqs.length,
        isLoading,
        searchLoading,
        allLoading,
        ssqs,
        filteredSsqs,
    ]);

    const select = (
        <FoodsSelectServerPagination
            selectedOption={value}
            options={filteredSsqs}
            onChange={(ssq) => onChange(ssq || null)}
            getId={(ssq: SsqOption) => ssq.id}
            getName={(ssq: SsqOption) => getSsqOptionName(ssq)}
            textOptions={{
                placeholder: placeholder || t("ssq.select.placeholder", "Search for an SSQ..."),
                searchPlaceholder: t("ssq.select.searchPlaceholder", "Search SSQ..."),
                emptyOptionsText: t("ssq.select.noOptions", "No SSQ available"),
                defaultValueText: t("ssq.select.defaultValue", "Default value"),
                noResult:
                    isLoading ? t("ssq.select.loading", "Loading...") : t("ssq.select.noResultFound", "No SSQ found"),
            }}
            search={search}
            setSearch={setSearch}
            isSearchFound={isSearchFound}
            disabled={disabled}
            BoxProps={{ width: "100%" }}
            BoxOptionsProps={{
                minWidth: theme.spacing(37.5),
                width: 0.5,
            }}
        />
    );

    return (
        <FormControl fullWidth>
            {appearance === "product-v2" ?
                <FoodsSelectTriggerAdapter accessibleLabel={accessibleLabel}>{select}</FoodsSelectTriggerAdapter>
            :   select}
        </FormControl>
    );
};
